import { Link } from "react-router-dom";
import { G_DARK, globalStyles } from "../components/Shared";

// Public sector bodies in the EU must publish an accessibility statement
// (Directive 2016/2102; in Austria the Web-Zugänglichkeits-Gesetz). This is
// the page it lives on. The wording below is a factual draft describing what
// was actually built — the compliance status and contact details need to be
// confirmed by whoever owns the portal before it is treated as the official
// statement.
const SUPPORTED = [
  "Every form field is labelled and reachable by keyboard.",
  "The file upload area can be operated with Enter or Space, not only by dragging.",
  "Upload progress, errors and confirmations are announced to screen readers.",
  "Text and controls meet a contrast ratio of at least 4.5:1 by default, and 7:1 in high-contrast mode.",
  "Status is never conveyed by colour alone — every state carries a word and a symbol.",
  "Motion is reduced automatically when your operating system asks for it.",
  "Text can be enlarged to 150% in the portal, and browser zoom works on top of that.",
];

const LIMITATIONS = [
  "The portal has not yet been audited by an external accessibility assessor.",
  "Tables of submissions, results and jobs are wide and scroll horizontally on small screens.",
  "Column resizing in tables is available with a pointer only; sorting is fully keyboard-accessible.",
  "Files you upload or download are not checked for accessibility — their content is whatever the depositor provided.",
];

export default function AccessibilityStatementPage() {
  const Section = ({ title, children }) => (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 10px" }}>{title}</h2>
      {children}
    </section>
  );
  const List = ({ items }) => (
    <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map(i => <li key={i} style={{ fontSize: 14, color: "#374151", lineHeight: 1.5 }}>{i}</li>)}
    </ul>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", fontFamily: "'DM Sans', sans-serif", padding: "40px 24px" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <style>{globalStyles}</style>

      <main style={{ maxWidth: 720, margin: "0 auto", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: "32px 36px" }}>
        <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 700 }}>Accessibility statement</h1>
        <p style={{ margin: "0 0 28px", fontSize: 14, color: "#6b7280" }}>
          BOKU Core Facility Bioinformatics — Project Portal
        </p>

        <Section title="What we aim for">
          <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: 0 }}>
            This portal is built to meet <strong>WCAG 2.2 Level AA</strong>, the standard referenced by
            EN 301 549 and by EU Directive 2016/2102 on the accessibility of public sector websites.
            An <strong>Accessibility</strong> panel, reachable from the sign-in screen and from the top bar
            once you are signed in, adds high contrast, larger text, reduced motion, larger click
            targets and a colour-blind-safe palette on top of that baseline.
          </p>
        </Section>

        <Section title="What works today">
          <List items={SUPPORTED} />
        </Section>

        <Section title="Known limitations">
          <List items={LIMITATIONS} />
        </Section>

        <Section title="Feedback">
          <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: 0 }}>
            If something on this portal is not usable for you, please tell us — describe the page and
            what happened, and we will look into it. Contact details for accessibility feedback are
            to be confirmed by the facility.
          </p>
        </Section>

        {/* inline-flex, not inline: the phone rule gives every link a 44px
            min-height, and min-height does nothing to an inline box. On its own
            line this is a control, not a word in a sentence (the mobile review
            measured it at 18px, 2026-09-30). */}
        <Link to="/login"
              style={{ fontSize: 14, color: G_DARK, fontWeight: 500, display: "inline-flex", alignItems: "center" }}>
          ← Back to sign in
        </Link>
      </main>
    </div>
  );
}
